import React, { useEffect, useState } from 'react';
import { QUERY_TYPE_LABELS } from './callUtils';

const ACTION_LABELS = {
  'admin.created': 'Admin created',
  'admin.deleted': 'Admin removed',
  'admin.agent_linked': 'Admin agent link updated',
  'admin.mobile_updated': 'Admin mobile number updated',
  'admin.avatar_updated': 'Admin photo updated',
  'admin.password_reset': 'Admin password reset',
  'role.created': 'Role created',
  'role.updated': 'Role updated',
  'role.deleted': 'Role deleted',
  'call.query_updated': 'Call query updated',
  'customer.profile_updated': 'Customer profile updated',
  'follow_up.created': 'Follow-up created',
  'follow_up.updated': 'Follow-up updated',
};

// One short human-readable line per action, from that entry's `details` JSON
// — falls back to raw JSON for any action not covered here, so a newly added
// action still shows something rather than nothing.
const formatDetails = (action, details) => {
  if (!details) return '-';
  switch (action) {
    case 'admin.created':
      return `username: ${details.username}${details.roleId ? `, role #${details.roleId}` : ''}`;
    case 'admin.deleted':
    case 'admin.password_reset':
      return details.username ? `username: ${details.username}` : '-';
    case 'admin.agent_linked':
      return details.agentName ? `linked to ${details.agentName}` : 'unlinked';
    case 'admin.mobile_updated':
      return details.mobileNumber ? `mobile: ${details.mobileNumber}` : 'mobile removed';
    case 'role.created':
    case 'role.updated':
    case 'role.deleted':
      return details.name ? `name: ${details.name}` : '-';
    case 'call.query_updated':
      return `type: ${QUERY_TYPE_LABELS[details.queryType] || details.queryType || '-'}${
        details.customerNumber ? `, customer: ${details.customerNumber}` : ''
      }`;
    case 'customer.profile_updated':
      return details.name ? `name: ${details.name}` : '-';
    case 'follow_up.created':
      return `customer: ${details.customerNumber}${details.followUpType ? `, type: ${details.followUpType}` : ''}`;
    case 'follow_up.updated':
      return `status: ${details.followUpStatus || '-'}`;
    default:
      return JSON.stringify(details);
  }
};

// Recent admin/role management and call-data-edit history, superadmin only —
// fetched lazily (only while this tab is actually mounted, see ManageAdmins)
// rather than on every Manage Admins load.
function AuditLog({ authFetch }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/audit-log');
        const data = await res.json();
        if (!cancelled) setEntries(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching audit log:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  if (loading) {
    return <div className="loading-state">Loading…</div>;
  }

  return (
    <div>
      <h2 className="agent-page-title">Audit Log</h2>
      {entries.length === 0 ? (
        <p className="agent-page-empty">No activity recorded yet.</p>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const detailsText = formatDetails(entry.action, entry.details);
                return (
                  <tr key={entry.id}>
                    <td>{new Date(entry.created_at).toLocaleString()}</td>
                    <td className="table-cell-strong">{entry.actor_username || '-'}</td>
                    <td>{ACTION_LABELS[entry.action] || entry.action}</td>
                    <td>{entry.target_type ? `${entry.target_type} #${entry.target_id}` : '-'}</td>
                    <td className="table-cell-truncate" title={detailsText}>
                      {detailsText}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AuditLog;
